import { useCallback, useState } from 'react'
import { Container } from '../../components/layout/Container'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { knowledgeApi } from '../../lib/api'
import { Upload, FileText, CheckCircle2, AlertCircle, Database, Cloud, HardDrive } from 'lucide-react'
import { cn } from '../../lib/utils'
import type { KnowledgeDocument, KnowledgeSource } from '../../types'

const connectors = [
  { id: 'postgres', name: 'PostgreSQL', icon: Database, status: 'No configurado' as const },
  { id: 'drive', name: 'Google Drive', icon: Cloud, status: 'No configurado' as const },
  { id: 'sharepoint', name: 'SharePoint', icon: HardDrive, status: 'No configurado' as const },
]

export function IngestPage() {
  const [isDragging, setIsDragging] = useState(false)
  const [queue, setQueue] = useState<(KnowledgeDocument & { status: 'uploading' | 'done' | 'error'; error?: string })[]>([])
  const [sources] = useState<KnowledgeSource[]>([])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const uploadFile = async (file: File) => {
    const tempId = `${Date.now()}-${file.name}`
    setQueue((prev) => [
      ...prev,
      {
        id: tempId,
        originalName: file.name,
        localFilePath: '',
        contentHash: '',
        confidentialityLevel: 1,
        fileSizeBytes: file.size,
        contentType: file.type,
        isActive: true,
        createdAt: new Date().toISOString(),
        status: 'uploading',
      },
    ])

    try {
      const uploaded = await knowledgeApi.uploadDocument(file)
      setQueue((prev) =>
        prev.map((item) =>
          item.id === tempId
            ? { ...uploaded, status: 'done' as const }
            : item
        )
      )
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al subir'
      setQueue((prev) =>
        prev.map((item) =>
          item.id === tempId ? { ...item, status: 'error', error: message } : item
        )
      )
    }
  }

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const files = Array.from(e.dataTransfer.files)
    files.forEach(uploadFile)
  }, [])

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    files.forEach(uploadFile)
    e.target.value = ''
  }, [])

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`
  }

  return (
    <Container size="wide" className="py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-text-primary">Ingesta de documentos</h1>
        <p className="mt-2 text-text-secondary">
          Sube archivos locales o conecta fuentes externas. El motor convertirá a Markdown y vectorizará el contenido.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <label
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={cn(
              'flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed p-12 text-center transition-colors',
              isDragging
                ? 'border-accent bg-accent/5'
                : 'border-border bg-surface hover:border-accent/30'
            )}
          >
            <input type="file" className="hidden" multiple onChange={handleFileInput} />
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-accent/10">
              <Upload className="h-8 w-8 text-accent-light" />
            </div>
            <h3 className="mt-4 text-lg font-medium text-text-primary">Haz clic o arrastra archivos aquí</h3>
            <p className="mt-1 text-sm text-text-muted">PDF, DOCX, XLSX, TXT, MD, CSV</p>
          </label>

          {queue.length > 0 && (
            <Card variant="elevated">
              <h3 className="mb-4 text-sm font-medium text-text-primary">Cola de procesamiento</h3>
              <div className="space-y-3">
                {queue.map((item) => (
                  <div key={item.id} className="flex items-center justify-between rounded-lg border border-border bg-bg p-3">
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-accent-light" />
                      <div>
                        <p className="text-sm font-medium text-text-primary">{item.originalName}</p>
                        <p className="text-xs text-text-muted">{formatBytes(item.fileSizeBytes)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {item.status === 'uploading' && (
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-accent border-t-transparent" />
                      )}
                      {item.status === 'done' && <CheckCircle2 className="h-5 w-5 text-emerald-400" />}
                      {item.status === 'error' && <AlertCircle className="h-5 w-5 text-error" />}
                      <span className="text-xs text-text-muted">
                        {item.status === 'uploading' && 'Procesando...'}
                        {item.status === 'done' && 'Listo'}
                        {item.status === 'error' && item.error}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>

        <div>
          <Card variant="elevated" className="h-full">
            <h3 className="mb-4 text-sm font-medium text-text-primary">Orígenes externos</h3>
            <div className="space-y-3">
              {connectors.map((connector) => {
                const Icon = connector.icon
                const isConnected = sources.some((s) => s.type.toLowerCase() === connector.id)
                return (
                  <div
                    key={connector.id}
                    className="flex items-center justify-between rounded-lg border border-border bg-bg p-3"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-surface-elevated">
                        <Icon className="h-4 w-4 text-accent-light" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-text-primary">{connector.name}</p>
                        <p className="text-xs text-text-muted">
                          {isConnected ? 'Conectado' : connector.status}
                        </p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" disabled>
                      {isConnected ? 'Configurar' : 'Conectar'}
                    </Button>
                  </div>
                )
              })}
            </div>
            <p className="mt-4 text-xs leading-relaxed text-text-muted">
              Los orígenes externos no se duplican físicamente; se conectan en tiempo real o se indexan vectorialmente en Supabase.
            </p>
          </Card>
        </div>
      </div>
    </Container>
  )
}
